import React, { useEffect, useRef, useState } from "react";
import TableHead, { TTableHeadProps, TTableLabel, TTHPropper, TTHProps } from "./TableHead";
import UserPref from "../UserPref";
import TableBody, { TTableBodyProps, TTableRowKey } from "./TableBody";
import { TTableRow } from "./TableConst";
import { TTableCellProps, TTableCellText, TTableCellValue } from "./TableCell";
import { TTableRowProps, } from "./TableRow";
import TableFooter from "./TableFooter";
import InfiniteScroll from "../Loading/InfiniteScroll";

export type { 
    TTableLabel, TTableCellValue, TTableCellText, TTableRowKey, TTHPropper, TTHProps, TTableHeadProps, TTableBodyProps, TTableRowProps, TTableCellProps, 
}
export type TTableRowCmp<T extends TTableRow> = (left:T,right:T,col:string,dir:number) => number
export type TTableSorter<T extends TTableRow> = (rows:T[], col:string, dir:number) => T[]
export type TTableProps<T extends TTableRow> = React.ComponentPropsWithoutRef<"table"> & Omit<TTableHeadProps,"sort"|"onSort"> & TTableBodyProps<T> & {
    tableName?:string
    rows:T[]
    footer?:T[]
    rowcmp?:TTableRowCmp<T>
    sortResolver?:string[]
    rowsPerPage?:number
    sorter?:TTableSorter<T>
}

export function DefaultTableRowComp<T extends TTableRow>(cellValue?:TTableCellValue<T>) {
    const value = cellValue || ((row:T,col:string) => row[col])
    return (l:T, r:T, col:string, dir:number) => {
        const left = value(l, col) , right = value(r, col)
        if (typeof(left)=="string" && typeof(right)=="string") return left.localeCompare(right)*dir
        else if (left instanceof Date && right instanceof Date) return (left.getTime()-right.getTime())*dir
        else return (left<right ? -dir : dir)
    }
}
export function DefaultTableSort<T extends TTableRow>(rows:T[], col:string, cellValue:TTableCellValue<T>|undefined, sortResolver:string[]|undefined, rowcmp?:TTableRowCmp<T>){
    if (!col) return rows
    const dir = col[0]=="-" ? -1 : 1
    const s_col = dir<0 ? col.substring(1) : col
    const value = cellValue || ((row:T,col:string) => row[col])
    const comp = rowcmp || DefaultTableRowComp(value)
    return rows.sort((l:T,r:T) => {
        var c = comp(l, r, s_col, dir)
        if (c || !sortResolver) return c
        else {
            for(const col of sortResolver){
                if (col==s_col) continue
                c = comp(l, r, col, 1)
                if (c) return c
            }
            return 0
        }
    })    
}
function getSort(key:string) {
    const sort = UserPref.i.get(key) || ""
    if (sort[0]=="-") return {sort:sort.substring(1), dir:-1}
    else return {sort, dir:1}
}
export default function Table<T extends TTableRow>(props:TTableProps<T>) {
    const {tableName,rows,cols,label,rowKey,cellValue,cellText,tr,td,th,htr,disableSort,footer,sortResolver,rowsPerPage,rowcmp,sorter,...tableProps} = props
    const sortKey = props.tableName ? `${props.tableName}_sort` : "table_sort"
    const sort_dir = getSort(sortKey)
    const [sort,setSort] = useState<string>(sort_dir.sort)
    const [sortedRows,setSortedRows] = useState<T[]>(sorter ? sorter(rows, sort, sort_dir.dir) : DefaultTableSort(props.rows, sort, cellValue, sortResolver, rowcmp))
    const [page,setPage] = useState<number>(1)
    const mounted = useRef<boolean>(false)

    const onSort = (sort:string) => {
        setSort(sort)
        UserPref.i.set(sortKey, sort)
        setSortedRows(sorter ? sorter(rows, sort, sort[0]=="-" ? -1 : 1) : DefaultTableSort(props.rows, sort, cellValue, sortResolver, rowcmp))
    }

    useEffect(() => {
        if (mounted.current) onSort(sort)
        else mounted.current = true
    },[rows])

    const filteredRows = rowsPerPage ? sortedRows.slice(0, page*rowsPerPage) : sortedRows
    const onScroll = () => setPage(page => page+1)
    return <table {...tableProps}>
        <TableHead cols={cols} label={label} htr={htr} th={th} onSort={onSort} sort={sort} disableSort={disableSort}/>
        <TableBody rows={filteredRows} cols={cols} rowKey={rowKey} tr={tr} td={td} cellValue={cellValue} cellText={cellText}/>
        {rowsPerPage && filteredRows.length<sortedRows.length ? <tfoot><tr><td><InfiniteScroll onScroll={onScroll}/></td></tr></tfoot> : <></>}
        {footer ? <TableFooter footer={footer} cols={cols} rowKey={rowKey} tr={tr} td={td} cellValue={cellValue} cellText={cellText}/> : <></>}
    </table>
}